import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import ImageWithPlaceholder from '../components/ImageWithPlaceholder';
import BackToTop from '../components/BackToTop';

export default function MarketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [market, setMarket] = useState(null);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/markets/${id}`, {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setMarket({
            id: data.id,
            name: data.name,
            address: data.address,
            daysOpen: data.daysOpen,
            operatingHours: data.operatingHours,
            imageUrl: data.imageUrl,
            vendorLimit: data.vendorLimit,
            currentVendorCount: data.currentVendorCount,
          });
          setVendors(data.vendors || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500 dark:text-slate-400">Loading market details…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Market Details</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      </div>
    );
  }

  if (!market) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Market Details</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">Market not found.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-8">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="self-start inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
      >
        <span aria-hidden>←</span>
        All markets
      </button>
      <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="aspect-[21/9] w-full bg-slate-200 dark:bg-slate-700">
          {market.imageUrl ? (
            <ImageWithPlaceholder
              src={market.imageUrl}
              alt={market.name}
              className="w-full h-full object-cover"
              containerClassName="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <img src="/icons8-online-store-100.png" alt="" className="w-24 h-24 opacity-60" />
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col items-center text-center gap-2">
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-50">{market.name}</h1>
          {market.address && (
            <p className="text-slate-600 dark:text-slate-400">{market.address}</p>
          )}
          {(market.daysOpen || market.operatingHours) && (
            <p className="text-sm text-slate-500">
              {[market.daysOpen, market.operatingHours].filter(Boolean).join(' · ')}
            </p>
          )}
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">
            {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'} in this market
          </p>
        </div>
      </div>

      <h2 className="text-xl font-semibold">Vendors</h2>

      {vendors.length === 0 ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
          <p className="text-lg text-slate-600 dark:text-slate-400">No vendors in this market yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {vendors.map((vendor) => (
            <button
              key={vendor.id}
              type="button"
              onClick={() => navigate(`/vendor/${vendor.id}`)}
              className="text-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden hover:border-emerald-500 transition-colors"
            >
              <div className="aspect-[16/10] bg-slate-100 dark:bg-slate-700">
                {vendor.storeImageUrl ? (
                  <ImageWithPlaceholder
                    src={vendor.storeImageUrl}
                    alt={vendor.storeName || 'Vendor'}
                    className="w-full h-full object-cover"
                    containerClassName="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                    Vendor booth
                  </div>
                )}
              </div>
              <div className="p-4 flex flex-col items-center gap-1">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                  {vendor.storeName || 'Unnamed Store'}
                </h3>
                {vendor.locationText && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">Booth {vendor.locationText}</p>
                )}
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mt-2">
                  View store →
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
      <BackToTop />
    </div>
  );
}
